import { useSearchParams } from 'react-router-dom'
import { useEffect, useMemo } from 'react'

import { useFormatMessage } from 'lib/localization'
import routesConfig from 'lib/routesConfig'
import { isEmailValid } from 'lib/validation'

const MAX_EMAIL_LENGTH = 254

const sanitizeEmailForDisplay = (value: string | null): string => {
  if (!value || value.length > MAX_EMAIL_LENGTH) return ''
  return isEmailValid(value) ? value : ''
}

export interface ErrorParamConfig {
  defaultError?: boolean
  stayOnRoute?: boolean
  /** Error codes whose idp_error_description / error_description is forwarded; every code when omitted */
  showDescriptionFor?: string[]
  setTopErrorMessage: (message: string) => void
  setTopErrorDescription: (message: string) => void
}

const BASE_I18N_KEY = 'ess'

export const useErrorParam = (config: ErrorParamConfig) => {
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const [params, setParams] = useSearchParams()
  const error = params.get('error')
  const idp_error = params.get('idp_error')
  const errorDescription = params.get('error_description')
  const idpErrorDescription = params.get('idp_error_description')
  const email = params.get('email')

  const errorMap: Map<string, string> = useMemo(
    () =>
      new Map([
        ['OAuth2AuthorizeParameterMissing', 'oauth2.exception.OAuth2AuthorizeParameterMissing'],
        ['OAuth2AuthorizationCodeRequestFailed', 'oauth2.exception.OAuth2AuthorizationCodeRequestFailed'],
        ['OAuth2IdentityProviderFlowFailed', 'oauth2.exception.OAuth2IdentityProviderFlowFailed'],
        ['WizardServiceFailed', 'oauth2.exception.WizardServiceFailed'],
        ['OAuth2TokenExchangePKCEVerificationFailed', 'oauth2.exception.OAuth2TokenExchangePKCEVerificationFailed'],
        ['IdTokenGenerationFailed', 'oauth2.exception.IdTokenGenerationFailed'],
        ['EGDIdentityNotFound', 'oauth2.exception.EGDIdentityNotFound'],
        ['OAuth2AuthorizeRedirectURINotAllowed', 'oauth2.exception.OAuth2AuthorizeRedirectURINotAllowed'],
        ['token_failed', 'oauth2.exception.azure_ad_api_error_token_failed'],
        ['env_missing', 'oauth2.exception.azure_ad_api_error_env_missing'],
        ['invalid_request', 'oauth2.exception.ms_invalid_request'],
        ['unauthorized_client', 'oauth2.exception.ms_unauthorized_client'],
        ['access_denied', 'oauth2.exception.ms_access_denied'],
        ['unsupported_response_type', 'oauth2.exception.ms_unsupported_response_type'],
        ['server_error', 'oauth2.exception.ms_server_error'],
        ['temporarily_unavailable', 'oauth2.exception.ms_temporarily_unavailable'],
        ['invalid_resource', 'oauth2.exception.ms_invalid_resource'],
        ['login_required', 'oauth2.exception.ms_login_required'],
        ['interaction_required', 'oauth2.exception.ms_interaction_required'],
        ['invalid_grant', 'oauth2.exception.ms_invalid_grant'],
        ['invalid_client', 'oauth2.exception.ms_invalid_client'],
        ['unsupported_grant_type', 'oauth2.exception.ms_unsupported_grant_type'],
        ['consent_required', 'oauth2.exception.ms_consent_required'],
        ['invalid_scope', 'oauth2.exception.ms_invalid_scope'],
        ['oauth2.auth_code_flow.token_exchange_failed', 'oauth2.auth_code_flow.token_exchange_failed'],
        ['Invalid_domain', 'oauth2.exception.tdf_invalid_domain'],
        ['This_domain_is_not_verified', 'oauth2.exception.tdf_domain_not_verified'],
        ['OAuth2ProcessNotFound', 'oauth2.exception.OAuth2ProcessNotFound'],
        ['user_cancel', 'oauth2.exception.user_cancel'],
        ['ExistingAccountError', 'oauth2.exception.ExistingAccountError'],
        ['DuplicateAccountError', 'oauth2.exception.DuplicateAccountError'],
        ['AccessTokenExpired', 'oauth2.exception.AccessTokenExpired'],
        ['InvalidClient', 'oauth2.exception.InvalidClient'],
        ['default', 'oauth2.exception.default'],
        // Login App - APIGW error codes
        ['LoginAppTokenExchangeFailed', 'oauth2.exception.LoginAppTokenExchangeFailed'],
        ['NoDefaultAccount', 'oauth2.exception.NoDefaultAccount'],
        ['NoEGDEntitlement', 'oauth2.exception.NoEGDEntitlement'],
        ['AccountRegionNotFound', 'oauth2.exception.AccountRegionNotFound'],
        ['UnknownRegion', 'oauth2.exception.UnknownRegion'],
        ['AccountNotFoundLocally', 'oauth2.exception.AccountNotFoundLocally'],
        ['CPLCircuitBreakerTripped', 'oauth2.exception.CPLCircuitBreakerTripped'],
        ['InvalidNavToken', 'oauth2.exception.InvalidNavToken'],
        ['InvalidAccountHint', 'oauth2.exception.InvalidAccountHint'],
        ['AmbiguousAccountContext', 'oauth2.exception.AmbiguousAccountContext'],
        ['InvalidEntryPath', 'oauth2.exception.InvalidEntryPath'],
        // Login App - TDF error codes
        ['session_creation_failed', 'oauth2.exception.session_creation_failed'],
        ['missing_auth_process_id', 'oauth2.exception.missing_auth_process_id'],
        ['missing_account', 'oauth2.exception.missing_account'],
        ['internal_error', 'oauth2.exception.internal_error'],
        ['invalid_state', 'oauth2.exception.invalid_state'],
        ['session_expired', 'oauth2.exception.session_expired'],
        ['auth_failed', 'oauth2.exception.auth_failed']
      ]),
    []
  )

  useEffect(() => {
    if (error) {
      const mappedError = errorMap.get(error)

      let mappedIdpError = idp_error && errorMap.get(idp_error)
      if (config.defaultError && !mappedIdpError) {
        mappedIdpError = errorMap.get('default')
      }

      const safeEmail = sanitizeEmailForDisplay(email ?? null)
      const messageData = { email: safeEmail }

      let displayError = idp_error || error
      if (mappedIdpError) {
        displayError = formatMessage(mappedIdpError, messageData)
      } else if (mappedError) {
        displayError = formatMessage(mappedError, messageData)
      }

      // Hardcoded scenario for AADSTS65004 microsoft code
      if (idpErrorDescription?.includes('AADSTS65004')) {
        const errMap = errorMap.get('user_cancel')
        if (errMap) {
          displayError = formatMessage(errMap)
        }
      }

      config.setTopErrorMessage(displayError)
      // BNESS-32535: callers may limit the raw provider/backend description to specific error codes
      const showDescription = !config.showDescriptionFor || config.showDescriptionFor.includes(error)
      config.setTopErrorDescription(showDescription ? idpErrorDescription || errorDescription || '' : '')
      params.delete('error')
      params.delete('idp_error')
      params.delete('error_description')
      params.delete('idp_error_description')
      params.delete('email')
      if (config.stayOnRoute) {
        setParams(params, { replace: true })
      } else {
        routesConfig.LOGIN.softGoto(undefined, false, `?${params.toString()}`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
